import React, { useState, useRef, useEffect } from "react";

const JsonPanel = ({ data, displayMode = false }) => {
    const [copied, setCopied] = useState(false);
    const [isHoveringCopy, setIsHoveringCopy] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const copyJson = async () => {
        if (!data) return;
        const text = JSON.stringify(data, null, 2);
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.warn("Clipboard copy failed", e);
        }
    };

    return (
        <div
            className={`flex-1 overflow-y-auto bg-background app-scrollbar ${
                displayMode ? "p-6" : "p-5"
            }`}
            style={{
                flex: displayMode ? "1 1 0%" : undefined,
                minWidth: displayMode ? "360px" : 0,
            }}
        >
            <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-border">
                <h3
                    className={`m-0 text-foreground font-bold tracking-tight ${
                        displayMode ? "text-[20px]" : "text-[15px]"
                    }`}
                >
                    Message JSON
                </h3>
                {data && (
                    <div className="flex items-center gap-2.5">
                        <button
                            onClick={copyJson}
                            onMouseEnter={() => setIsHoveringCopy(true)}
                            onMouseLeave={() => setIsHoveringCopy(false)}
                            disabled={copied}
                            className={`px-4 py-2 rounded-lg border-none text-white font-semibold text-[13px] transition-all duration-200 flex items-center gap-1.5 shadow-md ${
                                copied
                                    ? "bg-accent cursor-default shadow-lg"
                                    : isHoveringCopy
                                      ? "bg-secondary cursor-pointer -translate-y-px"
                                      : "bg-primary cursor-pointer"
                            }`}
                        >
                            {copied ? (
                                <>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M20 6L9 17l-5-5"
                                            stroke="#ffffff"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span>Copied!</span>
                                </>
                            ) : (
                                <>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            x="9"
                                            y="9"
                                            width="11"
                                            height="11"
                                            rx="2"
                                            stroke="#ffffff"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                                            stroke="#ffffff"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span>Copy JSON</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {!data ? (
                <div
                    className={`card text-center border-2 border-dashed text-muted-foreground ${
                        displayMode ? "p-16 text-[17px]" : "p-15"
                    }`}
                >
                    <div className="w-12 h-12 mx-auto mb-4 opacity-90">
                        {/* Document icon (no emoji) */}
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
                                className="fill-border"
                            />
                            <path d="M13 3v5h5" className="fill-border" />
                        </svg>
                    </div>
                    <div
                        className={`font-semibold mb-2 text-foreground ${
                            displayMode ? "text-[19px]" : "text-[15px]"
                        }`}
                    >
                        No message selected
                    </div>
                    <div
                        className={
                            displayMode
                                ? "text-[16px] leading-relaxed"
                                : "text-[13px] leading-relaxed"
                        }
                    >
                        Select a message from the list to view its complete JSON
                        structure here.
                    </div>
                </div>
            ) : (
                <div
                    className={`card rounded-lg border border-border/10 shadow-lg relative overflow-hidden ${
                        displayMode ? "p-6" : "p-5"
                    }`}
                    style={{ background: "var(--color-background)" }}
                >
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
                    <pre
                        className={`whitespace-pre-wrap break-words m-0 font-mono ${
                            displayMode
                                ? "text-[17px] leading-8"
                                : "text-[13px] leading-relaxed"
                        }`}
                        style={{ color: "var(--color-lightgrey)" }}
                    >
                        {JSON.stringify(data, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default JsonPanel;
